import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import CheckCircle from "@mui/icons-material/CheckCircle";
import Person from "@mui/icons-material/Person";
import Cake from "@mui/icons-material/Cake";
import Wc from "@mui/icons-material/Wc";
import Phone from "@mui/icons-material/Phone";
import LocationOn from "@mui/icons-material/LocationOn";
import Description from "@mui/icons-material/Description";
import {
  completeFitbitCollectionProfile,
  type FitbitCollectionProfile,
} from "../../services/fitbitCollectionEnrollmentService";

type ProfileForm = Omit<FitbitCollectionProfile, "age"> & { age: string };

const fieldIcon = (icon: React.ReactNode) => ({
  startAdornment: <InputAdornment position="start" sx={{ color: 'text.secondary' }}>{icon}</InputAdornment>,
});

const EMPTY_PROFILE: ProfileForm = {
  firstName: "",
  lastName: "",
  age: "",
  gender: "",
  phoneNumber: null,
  country: null,
  state: null,
  city: null,
  addressLine1: null,
  addressLine2: null,
  postalCode: null,
  additionalInfo: null,
};

function optionalText(value: string | null): string | null {
  const normalized = value?.trim();
  return normalized ? normalized : null;
}

/**
 * Fresh Fitbit profile completion. It deliberately has no participant ID,
 * study code, email, authorization header, documents, or legacy session data.
 */
type ProfileProps = {
  sourceLabel?: string;
  completeProfile?: (profile: FitbitCollectionProfile, profileSession?: string) => Promise<unknown>;
  initialProfile?: Partial<ProfileForm>;
  extraFields?: React.ReactNode;
};
const FitbitCollectionProfileCompletion: React.FC<ProfileProps> = ({ sourceLabel = 'Fitbit',
  completeProfile = completeFitbitCollectionProfile, initialProfile, extraFields }) => {
  // This non-secret selector stays in this tab's URL for reloads. The API
  // still requires its matching HttpOnly cookie; another study's tab cannot
  // overwrite this form's enrollment context.
  const [profileSession] = useState(() => {
    const values = new URLSearchParams(window.location.hash.slice(1)).getAll("profileSession");
    return values.length === 0 ? undefined : values.length === 1 ? values[0] : "";
  });
  const [form, setForm] = useState<ProfileForm>(() => ({ ...EMPTY_PROFILE, ...initialProfile }));
  const [submitting, setSubmitting] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [error, setError] = useState("");

  const update = (field: keyof ProfileForm) =>
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setForm((current) => ({
        ...current,
        [field]: event.target.value,
      } as ProfileForm));
    };

  const submit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const age = Number(form.age);
    if (!form.firstName.trim() || !form.lastName.trim() ||
      !form.gender.trim() || !Number.isSafeInteger(age) || age < 1 || age > 130) {
      setError("Enter your first name, last name, whole-number age, and gender.");
      return;
    }

    setSubmitting(true);
    setError("");
    try {
      await completeProfile({
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        age,
        gender: form.gender.trim(),
        phoneNumber: optionalText(form.phoneNumber),
        country: optionalText(form.country),
        state: optionalText(form.state),
        city: optionalText(form.city),
        addressLine1: optionalText(form.addressLine1),
        addressLine2: optionalText(form.addressLine2),
        postalCode: optionalText(form.postalCode),
        additionalInfo: optionalText(form.additionalInfo),
      }, profileSession);
      setCompleted(true);
    } catch (_error) {
      setError("We could not complete your enrollment. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (completed) {
    return (
      <Container maxWidth="sm">
        <Box sx={{ py: 6 }}>
          <Paper elevation={3} sx={{ p: 4, textAlign: "center" }}>
            <CheckCircle sx={{ fontSize: 64, color: "success.main", mb: 2 }} />
            <Typography variant="h4" component="h1" sx={{ fontWeight: 600, mb: 2 }}>
              Enrollment complete
            </Typography>
            <Typography color="text.secondary">
              Your {sourceLabel} connection is complete. Historical collection has
              been queued for this study.
            </Typography>
          </Paper>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      <Box sx={{ py: 4 }}>
        <Paper elevation={3} sx={{ p: { xs: 2, sm: 4 } }}>
          <Box sx={{ mb: 4, textAlign: "center" }}>
            <Person sx={{ fontSize: 48, color: "primary.main", mb: 2 }} />
            <Typography variant="h4" component="h1" sx={{ fontWeight: 600, mb: 1 }}>
              Complete your profile
            </Typography>
            <Typography color="text.secondary">
              Provide the profile details required for your study enrollment.
            </Typography>
          </Box>

          {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

          <Box component="form" onSubmit={submit} noValidate>
            {extraFields}
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2, mb: 2 }}>
              <TextField required label="First name" value={form.firstName} onChange={update("firstName")} autoComplete="given-name" InputProps={fieldIcon(<Person />)} />
              <TextField required label="Last name" value={form.lastName} onChange={update("lastName")} autoComplete="family-name" InputProps={fieldIcon(<Person />)} />
              <TextField required label="Age" type="number" value={form.age} onChange={update("age")} inputProps={{ min: 1, max: 130, step: 1 }} InputProps={fieldIcon(<Cake />)} />
              <TextField required label="Gender" value={form.gender} onChange={update("gender")} autoComplete="sex" InputProps={fieldIcon(<Wc />)} />
              <TextField label="Phone number (optional)" value={form.phoneNumber ?? ""} onChange={update("phoneNumber")} autoComplete="tel" InputProps={fieldIcon(<Phone />)} />
            </Box>
            <Typography variant="h6" component="h2" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <LocationOn /> Location
            </Typography>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2, mb: 3 }}>
              <TextField label="Country (optional)" value={form.country ?? ""} onChange={update("country")} autoComplete="country-name" />
              <TextField label="State or province (optional)" value={form.state ?? ""} onChange={update("state")} autoComplete="address-level1" />
              <TextField label="City (optional)" value={form.city ?? ""} onChange={update("city")} autoComplete="address-level2" />
              <TextField label="Address line 1 (optional)" value={form.addressLine1 ?? ""} onChange={update("addressLine1")} autoComplete="address-line1" />
              <TextField label="Address line 2 (optional)" value={form.addressLine2 ?? ""} onChange={update("addressLine2")} autoComplete="address-line2" />
              <TextField label="Postal code (optional)" value={form.postalCode ?? ""} onChange={update("postalCode")} autoComplete="postal-code" />
            </Box>
            <TextField
              fullWidth
              multiline
              minRows={3}
              label="Additional information (optional)"
              value={form.additionalInfo ?? ""}
              onChange={update("additionalInfo")}
              InputProps={{ startAdornment: <Description sx={{ mr: 1, mt: 1, color: 'text.secondary', alignSelf: 'flex-start' }} /> }}
              sx={{ mb: 3 }}
            />
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <Button type="submit" variant="contained" size="large" disabled={submitting}>
                {submitting ? <CircularProgress size={24} color="inherit" /> : "Complete enrollment"}
              </Button>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default FitbitCollectionProfileCompletion;
